import { useEffect, useRef, useState } from "react";

/**
 * Barra de rolagem horizontal ESPELHADA no TOPO do quadro Kanban. O quadro tem
 * o scrollbar nativo de baixo escondido (classe kanban-x-hidden) e esta barra
 * fina acima faz o scroll — mais perto dos controles, sem precisar descer até o
 * fim de um quadro alto pra rolar pro lado.
 *
 * Evita o hack de transform:rotateX (que quebraria o @hello-pangea/dnd): em vez
 * disso, um div com overflow-x próprio + um filho da largura do conteúdo, com
 * scroll sincronizado bidirecionalmente ao quadro.
 */
export function KanbanTopScrollbar({ targetRef }: { targetRef: React.RefObject<HTMLElement | null> }) {
  const barRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ scroll: 0, client: 0 });
  const lock = useRef(false);

  useEffect(() => {
    const alvo = targetRef.current;
    const bar = barRef.current;
    if (!alvo || !bar) return;

    const medir = () => setDims({ scroll: alvo.scrollWidth, client: alvo.clientWidth });
    medir();

    // Espelha o scroll dos dois lados sem loop de eco (trava de 1 frame).
    const espelhar = (de: HTMLElement, para: HTMLElement) => () => {
      if (lock.current) return;
      lock.current = true;
      para.scrollLeft = de.scrollLeft;
      requestAnimationFrame(() => { lock.current = false; });
    };
    const barParaQuadro = espelhar(bar, alvo);
    const quadroParaBar = espelhar(alvo, bar);
    bar.addEventListener("scroll", barParaQuadro, { passive: true });
    alvo.addEventListener("scroll", quadroParaBar, { passive: true });

    // Recalcula a largura quando o quadro redimensiona ou muda de colunas.
    const ro = new ResizeObserver(medir);
    ro.observe(alvo);
    const mo = new MutationObserver(medir);
    mo.observe(alvo, { childList: true });

    return () => {
      bar.removeEventListener("scroll", barParaQuadro);
      alvo.removeEventListener("scroll", quadroParaBar);
      ro.disconnect();
      mo.disconnect();
    };
  }, [targetRef]);

  // Só faz sentido mostrar quando há conteúdo além da largura visível.
  const temOverflow = dims.scroll > dims.client + 2;

  return (
    // A largura acompanha o clientWidth do quadro (não 100%): o quadro tem barra
    // VERTICAL, que come ~8px do espaço visível. Sem isso a barra do topo teria um
    // curso 8px maior que o do quadro — os últimos 8px da coluna final ficariam
    // inalcançáveis e o clamp no fim do curso podia ecoar, puxando o quadro de volta.
    <div
      ref={barRef}
      className="overflow-x-auto overflow-y-hidden kanban-topbar shrink-0"
      style={{
        height: temOverflow ? undefined : 0,
        marginBottom: temOverflow ? 4 : 0,
        width: dims.client || undefined,
      }}
      aria-hidden
    >
      <div style={{ width: dims.scroll, height: 1 }} />
    </div>
  );
}
